<script lang="ts" setup>
import type { HeroContentProp } from '@nimbus/types'
import type { UIButtonProps } from '@core/components/UI/Button.vue'
import { GameAssetTypes, type GameClickEventEmit } from '@game/types'
import { ref, useGameAssets, shadeColor, computed, whenever, useHeroMedia, useHeroGame } from '#imports'

const props = defineProps<HeroContentProp>()
const emit = defineEmits<GameClickEventEmit>()

const getGradientColor = (color: string | undefined, opacity: number | undefined): string => {
    if (!color || opacity === 0) {
        return 'transparent'
    }

    if (opacity) {
        const alpha = opacity >= 1 ? opacity / 100 : opacity

        return shadeColor(color, 0, alpha)
    }

    return shadeColor(color, 0, 0.75)
}

const bgGradInner = computed(() => getGradientColor(props.content?.gradient_inner_color, props.content?.gradient_inner_opacity))
const bgGradOuter = computed(() => getGradientColor(props.content?.gradient_outer_color, props.content?.gradient_outer_opacity))
const bgGradient = computed(() => `radial-gradient(${bgGradInner.value}, ${bgGradOuter.value} 85%)`)

const { game, isClosed, gameLoading, startGame, cta, isMobile } = useHeroGame(() => props.content)

const { getHeroGatewayUrl } = useHeroMedia()

const gameSymbol = ref<string>()
const gameHeroBg = ref<string | undefined>('')
const heroServiceBgError = ref(false)
const gameHeroBgError = ref(false)

const heroServiceBg = computed(() => {
    if (!props.content?.background_image) return null

    return getHeroGatewayUrl(props.content.background_image, props.preset)
})

const heroBgImgProps = computed(() => {
    return {
        class: 'hero__bg fit-parent',
        height: isMobile ? 537 : 374,
        sizes: '720px sm:828px lg:964px xl:1306px',
        quality: '50',
        fit: 'crop',
        alt: game.value?.name || props.content.title,
        provider: props.preset !== 'local' ? 'cloudflare' : undefined,
    }
})

const videoSplash = computed(() => {
    if (!props.content?.splash_image) return undefined

    return getHeroGatewayUrl(props.content.splash_image, props.preset)
})

const videoPoster = computed(() => {
    if (!props.content?.video_poster_src) return undefined

    return getHeroGatewayUrl(props.content.video_poster_src, props.preset)
})

const customSymbol = computed(() => {
    if (!props.content?.custom_symbol) return undefined

    return getHeroGatewayUrl(props.content.custom_symbol, props.preset)
})

const { assetUrl } = useGameAssets()
const gameTileBg = computed(() => assetUrl(game.value, GameAssetTypes.TILE, true))

async function handleStart() {
    await startGame(g => emit('game-click', g))
}

whenever(game, (game) => {
    gameSymbol.value = assetUrl(game, GameAssetTypes.SYMBOL, true)

    if (!props.content?.background_image) {
        gameHeroBg.value = assetUrl(game, GameAssetTypes.HERO_BACKGROUND, true)
    }
}, {
    immediate: true,
    once: true
})
</script>

<template>
    <div data-test="hero" class="hero">
        <div class="hero__bg-wrap">
            <NuxtImg
                v-if="!heroServiceBgError && heroServiceBg"
                v-bind="heroBgImgProps"
                :preset="heroServiceBg ? preset : 'gameservice'"
                :src="heroServiceBg"
                @error="() => heroServiceBgError = true"
            />
            <ClientOnly v-else>
                <NuxtImg
                    v-if="!gameHeroBgError && gameHeroBg"
                    v-bind="heroBgImgProps"
                    preset="gameservice"
                    :src="gameHeroBg"
                    @error="() => gameHeroBgError = true"
                />
                <NuxtImg
                    v-else-if="gameTileBg"
                    v-bind="heroBgImgProps"
                    preset="gameservice"
                    :src="gameTileBg"
                />
            </ClientOnly>
        </div>

        <div
            class="hero__gradient fit-parent"
            :style="{ '--bg-gradient': bgGradient, '--bg-solid': props.content?.gradient_outer_color }"
        />
        <div class="hero__content">
            <div class="hero__headline">
                <!-- eslint-disable vue/no-v-html content is safe -->
                <p v-if="content.tagline" class="hero__tagline" v-html="content.tagline" />
                <p
                    v-if="content.title"
                    class="hero__title"
                    v-html="content.title"
                />
                <p v-if="content.subtitle" class="hero__subtitle" v-html="content.subtitle" />
                <!-- eslint-enable vue/no-v-html -->
            </div>

            <div class="hero__action">
                <div
                    v-if="content.usp_list"
                    class="hero__usp-list"
                    :class="{ 'hero__usp-list--hide-symbol': !customSymbol && !gameSymbol }"
                >
                    <NuxtImg
                        v-if="customSymbol"
                        provider="cloudflare"
                        :preset="preset"
                        class="hero__usp-image"
                        width="100"
                        height="100"
                        quality="50"
                        :src="customSymbol"
                        :alt="`${content.title} symbol`"
                        @error="customSymbol = undefined"
                    />
                    <NuxtImg
                        v-else-if="gameSymbol"
                        provider="cloudflare"
                        preset="gameservice"
                        class="hero__usp-image"
                        width="100"
                        height="100"
                        quality="50"
                        :src="gameSymbol"
                        :alt="`${game?.name} symbol`"
                        @error="gameSymbol = undefined"
                    />

                    <ul class="hero__usp-list-ul list-none">
                        <li v-for="(usp, index) in content.usp_list" :key="index">
                            <div class="hero__usp-icon">
                                <span class="icon icon--check" style="width: 12px; height: 9px;">
                                    <svg
                                        width="512"
                                        height="381.804"
                                        viewBox="0 0 512 381.804"
                                    >
                                        <path d="M173.9 439.4 7.5 273a25.6 25.6 0 0 1 0-36.2l36.2-36.2a25.6 25.6 0 0 1 36.2 0L192 312.69 432.1 72.6a25.6 25.6 0 0 1 36.2 0l36.2 36.2a25.6 25.6 0 0 1 0 36.2L210.1 439.4a25.6 25.6 0 0 1-36.2 0Z" transform="translate(0 -65.098)" fill="#fff" />
                                    </svg>
                                </span>
                            </div>
                            {{ usp }}
                        </li>
                    </ul>
                </div>

                <UIButton
                    as="cta"
                    class="hero__cta"
                    :class="{ 'hero__cta--no-text-shadow': !!content.button_text_color }"
                    size="xl"
                    :color="content.button_color as UIButtonProps['color']"
                    :text-color="content.button_text_color as UIButtonProps['textColor']"
                    :to="content.button_url"
                    @click.prevent="handleStart"
                >
                    <template v-if="gameLoading">
                        <UILoader :color="false" width="40px" height="40px" />
                    </template>

                    <template v-else>
                        {{ !isClosed ? content.button_text : isClosed }}
                    </template>

                    <template v-if="cta && cta.icon" #append>
                        <UIIcon
                            :name="cta.icon"
                            :size="cta.size"
                            color="inherit"
                        />
                    </template>
                </UIButton>
            </div>
        </div>
        <div class="hero__game-tile">
            <div class="hero__game-tile__ratio" />
            <NimbusHeroGameTile
                :game="game"
                :enable-video="content.enable_video"
                :video-id="content.video_id"
                :video-src="content.video_src"
                :video-poster-src="videoPoster"
                :video-splash="videoSplash"
                :preset="preset"
                @start-game="handleStart"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.hero {
    display: flex;
    flex-direction: column-reverse;
    position: relative;
    padding: 15px 15px 0;
    color: #fff;
    overflow: hidden;
    background-position: top center;
    background-repeat: no-repeat;
    background-size: cover;
    width: 100%;

    @include tablet(md) {
        display: flex;
        justify-content: space-between;
        padding: 45px 30px 0;
        flex-direction: row;
        align-items: flex-start;
        gap: 30px;
    }

    @include desktop {
        gap: 40px;
    }

    @include desktop-xl {
        align-items: flex-start;
        padding-top: 60px;
        padding-right: 55px;
        padding-left: 70px;
        gap: 60px;
    }

    &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 50%;
        background: var(--hero-bottom-gradient-mobile, none);
        pointer-events: none;
        z-index: 0;

        @include tablet(md) {
            background: var(--hero-bottom-gradient, none);
        }
    }

    &__gradient {
        background-image: var(--bg-gradient);
    }

    &__content {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        width: 100%;
        height: 100%;
        z-index: 1;

        @media (max-width: #{ $screen-md - 1 }) {
            align-items: center;
        }

        @include tablet(md) {
            width: 50%;
        }
    }

    &__headline {
        @media (max-width: #{ $screen-md - 1 }) {
            text-align: center;
        }
    }

    &__tagline,
    &__title,
    &__subtitle {
        margin: 0;
        text-shadow: $hero-headings-text-shadow;

        :deep(sup),
        :deep(sub) {
            font-size: 10px;
            font-weight: 600;
            text-shadow: $hero-sub-sup-text-shadow;
        }
    }

    &__tagline {
        font-size: 4.2vw;
        font-size: clamp(13px, 4.2vw, 16px);
        font-weight: 700;
        line-height: 1;
        margin-bottom: 5px;

        @include tablet(md) {
            font-size: 1.6vw;
            font-size: clamp(16px, 1.6vw, 20px);
            margin-bottom: 5px;
        }

        @include desktop-xl {
            font-size: 1.8vw;
            font-size: clamp(16px, 1.8vw, 22px);
            white-space: nowrap;
        }
    }

    &__title {
        position: relative;
        margin-bottom: 5px;
        color: $hero-game-title-color;
        font-size: 8vw;
        font-size: clamp(22px, 8vw, 30px);
        font-family: $brand-font-family;
        font-weight: 900;
        line-height: 1.1;
        text-align: center;

        @include mobile {
            font-size: 9vw;
            font-size: clamp(22px, 9vw, 32px);
        }

        @include tablet(md) {
            text-align: left;
        }

        @include desktop {
            font-size: 4vw;
            font-size: clamp(22px, 4vw, 34px);
        }

        @include desktop-xl {
            font-size: 4.4vw;
            font-size: clamp(22px, 4.4vw, 40px);
        }

        // Hack to enable forcing title in one line
        :deep(span) {
            white-space: nowrap;
        }
    }

    &__subtitle {
        font-family: $body-font-family;
        font-size: 4.25vw;
        font-size: clamp(13px, 4.25vw, 17px);
        font-weight: 700;
        line-height: 1.3;
        text-shadow: 1px 1px 2px rgb(0 0 0 / 50%);

        @include tablet(md) {
            font-size: 18px;
        }

        @include desktop-xl {
            font-size: 20px;
        }
    }

    &__action {
        display: flex;
        width: 100%;
        flex: 1;
        flex-direction: column;
        justify-content: flex-end;
        align-items: center;
        margin-bottom: 30px;

        @media (min-width: $screen-sm) {
            align-items: flex-start;
            justify-content: flex-end;
        }

        @include tablet(md) {
            width: 90%;
        }

        @include desktop-xl {
            margin-bottom: 40px;
        }

        &:hover {
            color: inherit;
            text-decoration: none;
        }

        &-image {
            width: 130px;
            height: 80px;
            max-width: 85%;
            object-position: bottom;
            object-fit: contain;

            @include tablet(md) {
                width: 183px;
                height: 113px;
            }
        }
    }

    &__cta {
        width: 100%;
        max-width: 300px;

        &:not(:hover, :focus) {
            transition: none;
        }

        @media (max-width: #{ $screen-md - 1 }) {
            width: calc(100% - 30px);
            max-width: 380px;
            align-self: center;
            margin-top: 5vw;
        }

        .hero__usp-list + & {
            margin-top: 0;
        }

        :deep(.btn__content) {
            padding: 0 15px;
            font-size: clamp($font-size-lg, 5vw, $font-size-xxl);
            white-space: nowrap;

            @include tablet {
                font-size: $font-size-lg;
            }
        }
    }

    &__usp-list {
        position: relative;
        padding: 15px 0 15px 15px;
        margin: 18px 0 5vw;
        width: 100%;
        font-size: $font-size-xs;
        font-weight: bold;
        border-radius: var(--border-radius, $border-radius);
        background-color: rgba(255, 255, 255, 0.15);
        z-index: 1;

        @media (min-width: $screen-xs) {
            font-size: $font-size-sm;
        }

        @include tablet(md) {
            width: max-content;
            margin: 18px 0 30px;
            max-width: 320px;
        }

        @include desktop {
            min-width: 400px;
            max-width: 440px;
        }

        @include desktop-xl {
            max-width: 570px;
        }

        li {
            position: relative;
            padding-left: 30px;
            padding-right: 15px;

            @include tablet(md) {
                padding-right: 70px;
                line-height: 1.15;
            }

            &:first-child {
                padding-right: 16.5vw;

                @media (min-width: $screen-sm) {
                    padding-right: 90px;
                }

                @include desktop {
                    padding-right: 110px;
                }

                @include desktop-xl {
                    padding-right: 120px;
                }
            }
        }

        li + li {
            margin-top: 15px;
        }

        &--hide-symbol {
            @media (max-width: #{ $screen-lg - 1 }) {
                img {
                    display: none;
                }

                li {
                    padding-right: 15px !important;
                }
            }
        }
    }

    &__usp-icon {
        position: absolute;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        left: 0;
        top: 50%;
        width: 23px;
        height: 23px;
        border-radius: 50%;
        margin-right: 5px;
        color: $hero-usp-check-color;
        background: $hero-usp-check-bg;
        transform: translateY(-50%);
    }

    &__usp-image {
        position: absolute;
        width: 16vw;
        max-width: 80px;
        height: auto;
        top: -32px;
        right: 10px;
        object-fit: contain;
        z-index: 0;

        @media (min-width: $screen-sm) {
            width: 20vw;
        }

        @include tablet(md) {
            width: 100px;
        }

        @include desktop {
            right: 15px;
        }

        @include desktop-xl {
            top: -40px;
            right: 20px;
            height: 100px;
        }
    }

    &__game-tile {
        position: relative;
        width: 100%;
        border-radius: var(--border-radius, $border-radius);
        margin-bottom: 20px;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);

        &__ratio {
            padding-top: 56.25%; // 16:9 aspect ratio
            height: 0;
            z-index: 0;
        }

        @include tablet(md) {
            max-width: 500px;
        }

        @include desktop {
            width: 527px;
        }
    }

    :deep(.hero__cta--no-text-shadow) {
        text-shadow: none !important;
    }

    .icon {
        display: inline-flex;

        svg {
            max-width: 100%;
            max-height: 100%;
            height: auto;
            color: inherit;
        }

        path {
            fill: currentColor;
        }
    }
}
</style>
